import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Lock, KeyRound, Crown, Sparkles, TrendingDown, Target, AlertTriangle,
  BookOpen, Wand2, Film, Music, Users, Building2, Check, Zap, Rocket,
  Database, Cpu, Palette, Ear, Type, Hand, Wallet, PieChart, ArrowRight,
  Download,
} from "lucide-react";
import { exportPitchPptx } from "@/lib/exportPitchPptx";

const APEX_PPTX_SLIDES = [
  { kicker: "// 01 · COVER & VISION", title: "Building the Disney of the digital-native era.", body: "A vertically integrated media holding company capturing 100% of the content value chain — from story inception to viral marketing to cinematic production. We build, market, and produce everything in-house under one IP umbrella.", bullets: ["5 operating subsidiaries · 100% owned", "100% IP + film rights retained", "85% functional MVP live", "4 diversified monetization channels"] },
  { kicker: "// 02 · THE STRUCTURAL PROBLEM IN LEGACY MEDIA", title: "Legacy media leaks value at every stage of the pipeline.", body: "Authors write books, publishers take 85%, third-party agencies burn budget on ineffective marketing, and Hollywood buys rights for pennies on the dollar.", bullets: ["Value Leakage · 85% captured by middlemen", "Fragmented Audiences · zero native short-form reach", "Capital Inefficiency · $M burned on unvalidated IP"] },
  { kicker: "// 03 · THE VERTICALLY INTEGRATED ECOSYSTEM", title: "One parent. Five subsidiaries. Zero outsourcing.", body: "Every subsidiary reinforces the others in a closed-loop IP-to-screen pipeline, 100% owned by Apex Media Group Holding Co.", bullets: ["Parent · Apex Media Group Holding Co · Master IP + Treasury + Licensing", "Sleepy Owl Publishing · D2C Publishing + Pre-Order Marketplace", "Dream CapCha Engine · SaaS Author Studio + VARK Reader + Auto-Marketing", "Sunfield Studio · Premium Cinematic · Film · Series · CGI", "Audio & Music Studio · Composition + Soundtracks + Audiobooks", "Talent Network & Agency · Voice + Screen talent across all lines"] },
  { kicker: "// 04 · DREAM CAPCHA + GROWTH ENGINE", title: "Every manuscript becomes a self-funding viral funnel.", body: "Dream CapCha auto-extracts high-impact story scenes, pairs them with AI art / voice / music, renders native 9:16 videos, and syndicates across every major short-form platform — converting views directly into pre-orders.", bullets: ["VARK · Visual · Auditory · Read/Write · Kinesthetic · age-adaptive UI", "Auto-generated 9:16 videos syndicated to TikTok · Reels · Shorts · Stories", "Manuscript → 9:16 Video · 4 Platforms → Pre-orders + Book Sales"] },
  { kicker: "// 05 · FEATURED IP · NEPHILIM (Cross-Subsidiary Demo)", title: "One IP asset. Every subsidiary pulls it forward.", body: "Real example generated live on the platform. Nephilim by Lao — a fantasy manuscript that becomes an animated Dream CapCha scene, then enters Sunfield's green-light queue on its own viral traction.", bullets: ["Sleepy Owl · manuscript + reader engagement metrics", "Dream CapCha · auto-generated cinematic scenes", "Sunfield Studio · green-light candidate · film / series", "Tags: Fantasy · Mythic · Celestial · Coming-of-Age", "100% rights retained by the Holding Co"] },
  { kicker: "// 06 · CONTENT PIPELINE · BOOK TO SCREEN", title: "One IP asset. Exponential yield. Zero licensing friction.", body: "Sunfield Studio only green-lights films / series with pre-validated viral traction from Sleepy Owl / Dream CapCha. 100% of master film, merchandise, soundtrack, and publishing rights stay in-house.", bullets: ["01 · Manuscript — Dream CapCha studio + AI assistance", "02 · Sleepy Owl Launch — D2C storefront + pre-orders + metrics", "03 · Vertical Marketing — automated 4-platform syndication", "04 · Sunfield Green-Light — only viral titles advance", "05 · Screen + Merch — film / series / soundtrack / merchandise"] },
  { kicker: "// 07 · PRODUCT READINESS · 85% LIVE MVP", title: "Infrastructure built and operational. Capital funds scale, not R&D.", body: "The ecosystem is already 85% live. Remaining 15% is polish, native app packages, and enterprise API infrastructure.", bullets: ["Sleepy Owl Web Marketplace — Stripe + pre-orders LIVE", "Dream CapCha Studio + Reader — OpenAI + ElevenLabs LIVE", "Marketing Pipeline Framework — vertical video workflow LIVE"] },
  { kicker: "// 08 · DUAL MONETIZATION MODEL", title: "Four recurring revenue streams. One holding company.", body: "Diversified across B2C, B2B, SaaS, and Entertainment Licensing — each subsidiary reinforces the others.", bullets: ["Stream 01 · SaaS · Creator Subscriptions (Dream CapCha AI suite)", "Stream 02 · D2C · Book & Pre-Order Sales (Sleepy Owl direct)", "Stream 03 · B2B · EdTech + Institutional Licensing (VARK model)", "Stream 04 · Music + Film Yield (Sunfield + Audio Studio royalties)"] },
  { kicker: "// 09 · THE ASK · CAPITAL ALLOCATION", title: "Seeking Seed / Series A to fuel the ecosystem.", body: "Capital is deployed 100% on growth + slate — the platform, IP, and pipelines are already built.", bullets: ["35% · Organic Growth & Marketing (vertical video engine + author acquisition)", "35% · Sunfield Studio Slate (book trailers · pilots · CGI development)", "20% · Platform Scaling · Final 15% (native app packages + enterprise API)", "10% · Talent & Audio Operations (in-house voice/screen + music publishing)", "Closing: \"We build interactive worlds, automate viral growth, bring them to the silver screen.\""] },
];

const PIN = "2353";
const STORAGE_KEY = "apex_pitch_unlocked";
const C = "#06b6d4";   // Cyan (enterprise/infrastructure)
const G = "#f59e0b";   // Gold (subsidiary value)
const E = "#10b981";   // Emerald (revenue)

// ============ PIN LOCK ============
function PinLock({ onUnlock }) {
  const [entered, setEntered] = useState(["", "", "", ""]);
  const [error, setError] = useState(false);

  const handleChange = (i, val) => {
    if (val && !/^\d$/.test(val)) return;
    const next = [...entered];
    next[i] = val;
    setEntered(next);
    setError(false);
    if (val && i < 3) document.getElementById(`apex-pin-${i + 1}`)?.focus();
    if (next.join("").length === 4) {
      if (next.join("") === PIN) {
        sessionStorage.setItem(STORAGE_KEY, "1");
        setTimeout(onUnlock, 300);
      } else {
        setError(true);
        setTimeout(() => setEntered(["", "", "", ""]), 400);
      }
    }
  };
  const handleKey = (i, e) => {
    if (e.key === "Backspace" && !entered[i] && i > 0) document.getElementById(`apex-pin-${i - 1}`)?.focus();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center relative overflow-hidden" data-testid="apex-pin-lock">
      <div className="absolute inset-0 te-grid-bg opacity-25" aria-hidden />
      <motion.div initial={{opacity:0,scale:0.6}} animate={{opacity:1,scale:1}} transition={{duration:2.2}} className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full blur-[140px]" style={{ background: `${C}30` }} />
      <motion.div initial={{opacity:0,scale:0.5}} animate={{opacity:1,scale:1}} transition={{duration:2.6,delay:0.3}} className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full blur-[140px]" style={{ background: `${G}20` }} />

      <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.8}} className="relative bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-3xl p-10 max-w-md w-full mx-6 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
        <div className="flex items-center justify-center mb-6">
          <div className="h-16 w-16 rounded-2xl flex items-center justify-center" style={{ background: `${C}15`, border: `1px solid ${C}66` }}>
            <Lock size={26} style={{ color: C }} />
          </div>
        </div>
        <div className="text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: C }}>// CONFIDENTIAL · PIN REQUIRED</div>
          <h1 className="font-display text-3xl font-bold tracking-tighter">APEX · Holding Co. Vault</h1>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed">
            Vertically integrated content &amp; media holding company. 8-slide investor deck + subsidiary architecture. Enter your access PIN.
          </p>
        </div>

        <div className="mt-8 flex items-center justify-center gap-3">
          {entered.map((v, i) => (
            <input
              key={i}
              id={`apex-pin-${i}`}
              data-testid={`apex-pin-${i}`}
              value={v}
              onChange={(e) => handleChange(i, e.target.value.slice(-1))}
              onKeyDown={(e) => handleKey(i, e)}
              inputMode="numeric"
              maxLength={1}
              type="password"
              autoComplete="off"
              className={`h-16 w-14 text-center font-display text-3xl font-bold rounded-2xl bg-slate-950 border-2 outline-none transition-all ${error ? "border-red-500 animate-pulse" : v ? "shadow-[0_0_15px_-3px_rgba(6,182,212,0.5)]" : "border-slate-800"}`}
              style={v && !error ? { borderColor: C } : {}}
            />
          ))}
        </div>

        {error && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} className="mt-4 text-center font-mono text-[11px] uppercase tracking-[0.22em] text-red-400">
            // ACCESS DENIED · INVALID PIN
          </motion.div>
        )}

        <div className="mt-8 pt-6 border-t border-slate-800/60 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
          <KeyRound size={11} /> Session-locked · closes with your browser tab
        </div>
      </motion.div>
    </div>
  );
}

// ============ SLIDE FRAME ============
function SlideFrame({ num, kicker, children, extraBg }) {
  return (
    <section className="relative py-16 lg:py-24 border-b border-slate-800/80 overflow-hidden" data-testid={`apex-slide-${num}`}>
      <div className="absolute inset-0 te-grid-bg opacity-20" aria-hidden />
      {extraBg}
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-80px"}} transition={{duration:0.7}}>
          <div className="font-mono text-xs uppercase tracking-[0.3em] mb-4" style={{ color: C }}>{kicker}</div>
        </motion.div>
        {children}
      </div>
    </section>
  );
}

// ============ SLIDE 1 · COVER ============
function SlideCover() {
  const flow = [
    { n: "Sleepy Owl", Icon: BookOpen, sub: "Publishing", c: G },
    { n: "Dream CapCha", Icon: Wand2, sub: "Interactive + Growth", c: C },
    { n: "Sunfield Studio", Icon: Film, sub: "Cinematic", c: E },
  ];
  return (
    <section className="relative overflow-hidden border-b border-slate-800/80" data-testid="apex-slide-1">
      <div className="absolute inset-0 te-grid-bg opacity-30" aria-hidden />
      <motion.div initial={{opacity:0,scale:0.6}} animate={{opacity:1,scale:1}} transition={{duration:2.4}} className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full blur-[140px]" style={{ background: `${C}25` }} />
      <motion.div initial={{opacity:0,scale:0.5}} animate={{opacity:1,scale:1}} transition={{duration:2.8,delay:0.4}} className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full blur-[140px]" style={{ background: `${G}20` }} />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-16 lg:pb-24">
        <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} transition={{duration:0.6}} className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em]" style={{ borderColor: `${C}66`, background: `${C}0F`, color: C, border: `1px solid ${C}66` }}>
          <Crown size={12} /> APEX · Master Holding Co. · Investor Deck · 01 / 08
        </motion.div>
        <motion.h1 initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.9,delay:0.1,ease:[0.22,1,0.36,1]}} className="mt-8 font-display text-4xl sm:text-6xl lg:text-8xl font-bold leading-[0.95] tracking-tighter max-w-6xl">
          Building the <span style={{ color: C }}>Disney</span> of the <span style={{ color: G }}>digital-native</span> era.
        </motion.h1>
        <motion.p initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.8,delay:0.25}} className="mt-8 text-base sm:text-lg text-slate-300 max-w-4xl leading-relaxed">
          A vertically integrated media holding company capturing <span style={{ color: G }} className="font-semibold">100% of the content value chain</span> — from story inception to viral marketing to cinematic production. Traditional media loses millions outsourcing software, marketing, and film adaptations. <span style={{ color: C }} className="font-semibold">We build, market, and produce everything in-house under one IP umbrella.</span>
        </motion.p>

        {/* Ecosystem flow */}
        <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:1,delay:0.4}} className="mt-12 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-3 items-center">
          {flow.map((f, i, arr) => (
            <div key={f.n} className="contents">
              <div className="rounded-2xl p-5 border-2 backdrop-blur-xl relative overflow-hidden" style={{ borderColor: `${f.c}55`, background: `linear-gradient(135deg, ${f.c}15, rgba(15,23,42,0.7))` }}>
                <div className="absolute -top-8 -right-8 h-24 w-24 rounded-full blur-2xl opacity-40" style={{ background: f.c }} aria-hidden />
                <div className="relative">
                  <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: `${f.c}1A`, border: `1px solid ${f.c}66` }}>
                    <f.Icon size={17} style={{ color: f.c }} />
                  </div>
                  <div className="mt-4 font-display text-lg font-bold text-slate-100">{f.n}</div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: f.c }}>{f.sub}</div>
                </div>
              </div>
              {i < arr.length - 1 && (
                <div className="hidden md:flex items-center justify-center">
                  <ArrowRight size={20} className="text-slate-600" />
                </div>
              )}
            </div>
          ))}
        </motion.div>

        {/* KPI row */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { k: "5", v: "operating subsidiaries", c: C },
            { k: "100%", v: "IP + film rights retained", c: G },
            { k: "85%", v: "functional MVP live", c: E },
            { k: "4", v: "monetization channels", c: C },
          ].map((s, i) => (
            <motion.div key={i} initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.6,delay:0.5+i*0.06}} className="border border-slate-800/80 bg-slate-900/60 backdrop-blur-xl rounded-2xl p-4">
              <div className="font-display text-4xl font-bold tracking-tighter" style={{ color: s.c }}>{s.k}</div>
              <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.22em] text-slate-400">{s.v}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============ SLIDE 2 · THE PROBLEM ============
function SlideProblem() {
  const problems = [
    { Icon: TrendingDown, title: "Value Leakage", desc: "Authors write books → Publishers take 85% → Third-party agencies run expensive, ineffective marketing → Hollywood buys rights for pennies on the dollar.", stat: "85%", statLabel: "captured by middlemen" },
    { Icon: AlertTriangle, title: "Fragmented Audiences", desc: "Static text fails to retain digital-native attention spans. Traditional channels can't reach TikTok, Reels, or Shorts organically — the audiences that actually matter.", stat: "0", statLabel: "native short-form reach" },
    { Icon: Target, title: "Capital Inefficiency", desc: "Independent production houses spend millions acquiring cold IP without pre-validated fanbases or automated social growth loops driving pre-orders and demand.", stat: "$M", statLabel: "burned on unvalidated IP" },
  ];
  return (
    <SlideFrame num={2} kicker="// 02 · THE STRUCTURAL PROBLEM IN LEGACY MEDIA">
      <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">Legacy media leaks value at <span style={{ color: C }}>every stage</span> of the pipeline.</h2>
      <div className="mt-10 grid md:grid-cols-3 gap-3">
        {problems.map((p, i) => (
          <motion.div key={i} initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-80px"}} transition={{duration:0.6,delay:i*0.08}} className="bg-slate-900/60 border border-red-900/40 backdrop-blur-xl rounded-2xl p-6">
            <div className="h-11 w-11 rounded-xl bg-red-950/50 border border-red-500/40 flex items-center justify-center"><p.Icon size={17} className="text-red-400" /></div>
            <h3 className="mt-4 font-display text-xl font-bold">{p.title}</h3>
            <p className="mt-2 text-[13px] text-slate-400 leading-relaxed">{p.desc}</p>
            <div className="mt-5 pt-4 border-t border-slate-800/60">
              <div className="font-display text-3xl font-bold text-red-400">{p.stat}</div>
              <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500">{p.statLabel}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </SlideFrame>
  );
}

// ============ SLIDE 3 · ECOSYSTEM ARCHITECTURE ============
function SlideEcosystem() {
  const subs = [
    { n: "Sleepy Owl Publishing", role: "D2C Publishing House · Author Launchpad · Pre-order Marketplace (Fiction & Historical Non-Fiction)", Icon: BookOpen, c: G },
    { n: "Dream CapCha Engine", role: "SaaS Interactive Author Studio · VARK Age-Adaptive Reader · Automated Vertical Short-Form Marketing Engine", Icon: Wand2, c: "#7c3aed" },
    { n: "Sunfield Studio", role: "Premium Cinematic Screen Production · Film · TV Series · CGI · Animatics", Icon: Film, c: E },
    { n: "Audio & Music Studio", role: "In-house Composition · Ambient Soundscapes · Soundtrack Publishing · Audiobooks", Icon: Music, c: "#ec4899" },
    { n: "Talent Network & Agency", role: "Voice Actors · Narrators · Screen Talent powering Audio, Video, and Marketing Feeds", Icon: Users, c: C },
  ];
  return (
    <SlideFrame num={3} kicker="// 03 · THE VERTICALLY INTEGRATED ECOSYSTEM">
      <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">One parent. <span style={{ color: C }}>Five subsidiaries.</span> Zero <span style={{ color: G }}>outsourcing.</span></h2>
      <p className="mt-4 text-slate-400 max-w-3xl leading-relaxed">Five synergistic operating subsidiaries owned <span style={{ color: C }} className="font-semibold">100% by the Parent Holding Company</span>. Every subsidiary reinforces the others in a closed-loop IP-to-screen pipeline.</p>

      {/* Parent card */}
      <motion.div initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.7}} className="mt-10 relative rounded-3xl p-6 lg:p-7 border-2 backdrop-blur-xl overflow-hidden" style={{ borderColor: `${C}66`, background: `linear-gradient(135deg, ${C}18, rgba(15,23,42,0.7))` }}>
        <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full blur-3xl opacity-40" style={{ background: C }} aria-hidden />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl flex items-center justify-center" style={{ background: `${C}1A`, border: `1px solid ${C}66` }}>
              <Crown size={22} style={{ color: C }} />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: C }}>// PARENT ENTITY</div>
              <div className="font-display text-2xl font-bold">Apex Media Group Holding Co.</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {["Master IP + Trademarks", "Central Treasury", "IP Licensing"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em]" style={{ background: `${C}0F`, border: `1px solid ${C}55`, color: C }}>
                <Check size={9} /> {t}
              </span>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Descent connector */}
      <div className="mt-2 flex justify-center">
        <div className="h-8 w-px" style={{ background: `linear-gradient(180deg, ${C}, transparent)` }} />
      </div>

      {/* Subsidiaries grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {subs.map((s, i) => (
          <motion.div key={i} initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-80px"}} transition={{duration:0.55,delay:i*0.08}} className="rounded-2xl p-5 border-2 backdrop-blur-xl" style={{ borderColor: `${s.c}55`, background: `linear-gradient(135deg, ${s.c}10, rgba(15,23,42,0.6))` }}>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: `${s.c}1A`, border: `1px solid ${s.c}66` }}>
                <s.Icon size={16} style={{ color: s.c }} />
              </div>
              <div className="font-mono text-[9px] uppercase tracking-[0.22em]" style={{ color: s.c }}>SUBSIDIARY {String(i + 1).padStart(2, "0")}</div>
            </div>
            <h3 className="mt-3 font-display text-base font-bold">{s.n}</h3>
            <p className="mt-2 text-[12px] text-slate-400 leading-relaxed">{s.role}</p>
          </motion.div>
        ))}
      </div>
    </SlideFrame>
  );
}

// ============ SLIDE 4 · DREAM CAPCHA + GROWTH ENGINE ============
function SlideTech() {
  const vark = [
    { l: "Visual", Icon: Palette, c: "#7c3aed" },
    { l: "Auditory", Icon: Ear, c: G },
    { l: "Read/Write", Icon: Type, c: E },
    { l: "Kinesthetic", Icon: Hand, c: "#ec4899" },
  ];
  const socials = ["TikTok", "Instagram Reels", "Facebook Stories", "YouTube Shorts"];
  return (
    <SlideFrame num={4} kicker="// 04 · TECH HIGHLIGHT · DREAM CAPCHA + GROWTH ENGINE">
      <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">Every manuscript becomes a <span style={{ color: G }}>self-funding viral funnel</span>.</h2>
      <p className="mt-4 text-slate-400 max-w-3xl leading-relaxed">Dream CapCha auto-extracts high-impact story scenes, pairs them with AI artwork, voice, and music, and renders native 9:16 video assets — then syndicates them across every major short-form platform. <span style={{ color: C }} className="font-semibold">Social views convert directly into pre-orders on the Sleepy Owl storefront.</span></p>

      <div className="mt-10 grid lg:grid-cols-2 gap-4">
        {/* VARK grid */}
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.7}} className="rounded-2xl p-6 border-2 backdrop-blur-xl" style={{ borderColor: `${C}55`, background: `linear-gradient(135deg, ${C}10, rgba(15,23,42,0.6))` }}>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: C }}>// INTERACTIVE STORY ENGINE · VARK MODEL</div>
          <h3 className="mt-2 font-display text-xl font-bold">Multi-sensory reading, tailored by age.</h3>
          <p className="mt-2 text-[12px] text-slate-400">Age-adaptive UI modes from 3-7 all the way to Adult.</p>
          <div className="mt-5 grid grid-cols-4 gap-2">
            {vark.map((v) => (
              <div key={v.l} className="rounded-xl p-3 border" style={{ background: `${v.c}10`, borderColor: `${v.c}55` }}>
                <v.Icon size={14} style={{ color: v.c }} />
                <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.18em]" style={{ color: v.c }}>{v.l}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Vertical growth engine */}
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.7,delay:0.1}} className="rounded-2xl p-6 border-2 backdrop-blur-xl relative overflow-hidden" style={{ borderColor: `${G}55`, background: `linear-gradient(135deg, ${G}10, rgba(15,23,42,0.6))` }}>
          <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full blur-3xl opacity-40" style={{ background: G }} aria-hidden />
          <div className="relative">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: G }}>// AUTOMATED VERTICAL VIDEO · THE SECRET SAUCE</div>
            <h3 className="mt-2 font-display text-xl font-bold">Manuscript → 9:16 video → 4 platforms.</h3>
            <p className="mt-2 text-[12px] text-slate-400">Story scene + AI artwork + voice clip + soundscape = native short-form video, generated and syndicated automatically.</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {socials.map((s) => (
                <span key={s} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.22em]" style={{ background: `${G}0F`, border: `1px solid ${G}55`, color: G }}>
                  <Zap size={9} /> {s}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Conversion strip */}
      <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6,delay:0.2}} className="mt-4 rounded-2xl p-5 backdrop-blur-xl border" style={{ borderColor: `${E}55`, background: `linear-gradient(90deg, ${G}10, ${E}10, ${C}10)` }}>
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto_1fr_auto] items-center gap-3 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-300">Manuscript</div>
          <ArrowRight size={16} className="mx-auto text-slate-500" />
          <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: G }}>9:16 Video · 4 Platforms</div>
          <ArrowRight size={16} className="mx-auto text-slate-500" />
          <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: E }}>Pre-orders + Book Sales</div>
        </div>
      </motion.div>
    </SlideFrame>
  );
}

// ============ SLIDE 5 · BOOK TO SCREEN PIPELINE ============
function SlidePipeline() {
  const stages = [
    { n: "01", title: "Manuscript", desc: "Author drafts inside the Dream CapCha studio with AI assistance.", Icon: BookOpen, c: "#7c3aed" },
    { n: "02", title: "Sleepy Owl Launch", desc: "D2C storefront, pre-orders, and reader engagement metrics captured.", Icon: Rocket, c: G },
    { n: "03", title: "Vertical Marketing", desc: "Auto-generated short-form videos syndicated across 4 social channels.", Icon: Wand2, c: C },
    { n: "04", title: "Sunfield Green-Light", desc: "Studio green-lights only titles with proven viral traction and reader conversion.", Icon: Film, c: E },
    { n: "05", title: "Screen + Merch", desc: "Film / series / soundtrack / merchandise — 100% of rights retained by the Holding Co.", Icon: Crown, c: G },
  ];
  return (
    <SlideFrame num={5} kicker="// 05 · CONTENT PIPELINE · FROM BOOK TO SCREEN">
      <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">One IP asset. <span style={{ color: G }}>Exponential yield.</span> Zero licensing friction.</h2>
      <p className="mt-4 text-slate-400 max-w-3xl leading-relaxed">Sunfield Studio only green-lights films and series with pre-validated viral traction from the Sleepy Owl / Dream CapCha engine. The Holding Company retains <span style={{ color: C }} className="font-semibold">100% of master film, merchandise, soundtrack, and publishing rights</span>.</p>

      <div className="mt-10 relative">
        <div className="hidden lg:block absolute top-6 left-6 right-6 h-px" style={{ background: `linear-gradient(90deg, #7c3aed, ${G}, ${C}, ${E}, ${G})` }} aria-hidden />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {stages.map((s, i) => (
            <motion.div key={i} initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-80px"}} transition={{duration:0.55,delay:i*0.08}} className="relative rounded-2xl p-5 border-2 backdrop-blur-xl" style={{ borderColor: `${s.c}55`, background: `linear-gradient(180deg, ${s.c}10, rgba(15,23,42,0.6))` }}>
              <div className="hidden lg:block absolute -top-1.5 left-6 h-3 w-3 rounded-full" style={{ background: s.c, boxShadow: `0 0 10px ${s.c}` }} />
              <div className="flex items-center justify-between">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: `${s.c}1A`, border: `1px solid ${s.c}66` }}>
                  <s.Icon size={15} style={{ color: s.c }} />
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: s.c }}>{s.n}</div>
              </div>
              <h3 className="mt-4 font-display text-base font-bold">{s.title}</h3>
              <p className="mt-2 text-[11px] text-slate-400 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6,delay:0.3}} className="mt-6 rounded-2xl p-5 border flex items-center gap-4" style={{ borderColor: `${G}55`, background: `${G}0A` }}>
        <div className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${G}1A`, border: `1px solid ${G}55` }}>
          <Crown size={18} style={{ color: G }} />
        </div>
        <div className="text-[13px] text-slate-200 leading-relaxed">
          <span style={{ color: G }} className="font-semibold">Zero licensing friction:</span> where legacy studios pay publishers, publishers pay authors, and Hollywood pays licensing agents — APEX pays no one. Every dollar of downstream revenue flows back to the Parent Holding Co.
        </div>
      </motion.div>
    </SlideFrame>
  );
}

// ============ SLIDE 6 · 85% MVP ============
function SlideMVP() {
  const assets = [
    { Icon: Wallet, title: "Sleepy Owl Web Marketplace", desc: "Live D2C storefront with Stripe payments, pre-order campaign engine, and open catalog access." },
    { Icon: Cpu, title: "Dream CapCha Studio + Reader", desc: "Active interactive HTML5 reader · AI-tailored UI layers · OpenAI + ElevenLabs API routes wired." },
    { Icon: Sparkles, title: "Marketing Pipeline Framework", desc: "Functional short-form video generation workflow ready for organic social syndication." },
  ];
  return (
    <SlideFrame num={6} kicker="// 06 · PRODUCT READINESS · THE 85% LIVE MVP">
      <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">Infrastructure is <span style={{ color: E }}>built and operational</span>. Capital funds <span style={{ color: G }}>scale</span>, not R&amp;D.</h2>

      <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.7}} className="mt-10 bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-3xl p-8">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: C }}>// ECOSYSTEM COMPLETION</div>
            <h3 className="mt-2 font-display text-3xl font-bold">85% functional MVP</h3>
          </div>
          <div className="text-right">
            <div className="font-display text-6xl lg:text-7xl font-bold tracking-tighter" style={{ color: C }}>85%</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400 mt-1">live · <span style={{color: G}}>15% remaining</span></div>
          </div>
        </div>
        <div className="h-5 rounded-full bg-slate-950 border border-slate-800 overflow-hidden">
          <motion.div initial={{width:0}} whileInView={{width:"85%"}} viewport={{once:true}} transition={{duration:1.6,ease:"easeOut"}} className="h-full rounded-full" style={{ background: `linear-gradient(90deg, ${C}, ${G})` }} />
        </div>
      </motion.div>

      <div className="mt-6 grid md:grid-cols-3 gap-3">
        {assets.map((a, i) => (
          <motion.div key={i} initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-80px"}} transition={{duration:0.55,delay:i*0.08}} className="rounded-2xl p-6 border-2 backdrop-blur-xl" style={{ borderColor: `${C}55`, background: `linear-gradient(135deg, ${C}10, rgba(15,23,42,0.6))` }}>
            <div className="flex items-center justify-between">
              <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: `${C}1A`, border: `1px solid ${C}66` }}>
                <a.Icon size={17} style={{ color: C }} />
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.22em] bg-emerald-500/10 border border-emerald-500/40 text-emerald-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE
              </span>
            </div>
            <h4 className="mt-4 font-display text-lg font-bold">{a.title}</h4>
            <p className="mt-2 text-[12px] text-slate-400 leading-relaxed">{a.desc}</p>
          </motion.div>
        ))}
      </div>
    </SlideFrame>
  );
}

// ============ SLIDE 7 · REVENUE ============
function SlideRevenue() {
  const streams = [
    { n: "SaaS · Creator Subscriptions", desc: "Monthly/annual fees for authors using the Dream CapCha AI story enhancement suite.", Icon: Cpu, c: "#7c3aed" },
    { n: "D2C · Book & Pre-Order Sales", desc: "Direct digital sales, pre-order deposits, and reader access passes on Sleepy Owl.", Icon: BookOpen, c: G },
    { n: "B2B · EdTech & Institutional Licensing", desc: "Classroom + library licensing powered by the VARK multi-modal learning model.", Icon: Building2, c: C },
    { n: "Music & Film Yield", desc: "Streaming royalties from Audio Studio compositions + box-office / streaming distribution via Sunfield Studio.", Icon: Film, c: E },
  ];
  return (
    <SlideFrame num={7} kicker="// 07 · DUAL MONETIZATION MODEL & PROJECTIONS">
      <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">Four <span style={{ color: C }}>recurring</span> revenue streams. <span style={{ color: G }}>One holding company.</span></h2>
      <p className="mt-4 text-slate-400 max-w-3xl leading-relaxed">Diversified across B2C, B2B, SaaS, and Entertainment Licensing — with each subsidiary reinforcing the others.</p>

      <div className="mt-10 grid md:grid-cols-2 gap-3">
        {streams.map((s, i) => (
          <motion.div key={i} initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-80px"}} transition={{duration:0.6,delay:i*0.08}} className="rounded-2xl p-6 border-2 backdrop-blur-xl relative overflow-hidden" style={{ borderColor: `${s.c}55`, background: `linear-gradient(135deg, ${s.c}12, rgba(15,23,42,0.6))` }}>
            <div className="absolute -top-8 -right-8 h-32 w-32 rounded-full blur-3xl opacity-30" style={{ background: s.c }} aria-hidden />
            <div className="relative flex items-start gap-4">
              <div className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${s.c}1A`, border: `1px solid ${s.c}66` }}>
                <s.Icon size={18} style={{ color: s.c }} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: s.c }}>STREAM 0{i + 1}</div>
                <h3 className="mt-1 font-display text-lg font-bold">{s.n}</h3>
                <p className="mt-2 text-[12px] text-slate-400 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </SlideFrame>
  );
}

// ============ SLIDE 8 · THE ASK ============
function SlideAsk() {
  const alloc = [
    { pct: 35, label: "Organic Growth & Marketing", desc: "Scaling the Dream CapCha vertical video generator and author acquisition pipeline.", c: C },
    { pct: 35, label: "Sunfield Studio Slate", desc: "Funding cinematic book trailers, short-form series pilots, and CGI development.", c: G },
    { pct: 20, label: "Platform Scaling · Final 15%", desc: "Native iOS/Android app store packages and enterprise API infrastructure.", c: E },
    { pct: 10, label: "Talent & Audio Operations", desc: "Expanding in-house voice/screen rosters and music publishing catalogs.", c: "#ec4899" },
  ];
  const g = `conic-gradient(${C} 0% 35%, ${G} 35% 70%, ${E} 70% 90%, #ec4899 90% 100%)`;
  return (
    <SlideFrame num={8} kicker="// 08 · THE ASK · CAPITAL ALLOCATION">
      <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">Seeking <span style={{ color: C }}>Seed / Series A</span> to fuel the <span style={{ color: G }}>ecosystem</span>.</h2>

      <div className="mt-10 grid lg:grid-cols-[1fr_1.5fr] gap-6 items-center">
        <motion.div initial={{opacity:0,scale:0.9}} whileInView={{opacity:1,scale:1}} viewport={{once:true}} transition={{duration:0.9}} className="flex flex-col items-center">
          <div className="relative w-72 h-72 rounded-full" style={{ background: g, boxShadow: `0 30px 60px -20px rgba(0,0,0,0.6)` }}>
            <div className="absolute inset-8 rounded-full bg-slate-950 flex flex-col items-center justify-center border-2 border-slate-800">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Use of funds</div>
              <div className="mt-1 font-display text-3xl font-bold" style={{ color: C }}>100%</div>
              <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500 text-center">deployed on<br/>growth + slate</div>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2 justify-center">
            {alloc.map((a) => (
              <span key={a.label} className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.22em]" style={{ background: `${a.c}0F`, border: `1px solid ${a.c}66`, color: a.c }}>
                <span className="h-2 w-2 rounded-full" style={{ background: a.c }} /> {a.pct}%
              </span>
            ))}
          </div>
        </motion.div>

        <div className="space-y-3">
          {alloc.map((a, i) => (
            <motion.div key={i} initial={{opacity:0,x:20}} whileInView={{opacity:1,x:0}} viewport={{once:true}} transition={{duration:0.6,delay:i*0.08}} className="rounded-2xl p-5 border-2 backdrop-blur-xl flex items-center gap-5" style={{ borderColor: `${a.c}55`, background: `linear-gradient(90deg, ${a.c}10, rgba(15,23,42,0.6))` }}>
              <div className="flex flex-col items-center justify-center h-16 w-16 rounded-2xl shrink-0" style={{ background: `${a.c}1A`, border: `1px solid ${a.c}66` }}>
                <div className="font-display text-2xl font-bold" style={{ color: a.c }}>{a.pct}%</div>
              </div>
              <div>
                <h4 className="font-display text-lg font-bold">{a.label}</h4>
                <p className="mt-1 text-[12px] text-slate-400 leading-relaxed">{a.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <motion.blockquote initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.8,delay:0.4}} className="mt-14 text-center relative">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="h-64 w-64 rounded-full blur-3xl opacity-30" style={{ background: C }} aria-hidden />
        </div>
        <div className="relative font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">// CLOSING</div>
        <p className="relative font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight max-w-4xl mx-auto">
          &ldquo;We don&apos;t just write stories. We <span style={{ color: C }}>build interactive worlds</span>, automate their <span style={{ color: G }}>viral growth</span>, and bring them to the <span style={{ color: E }}>silver screen</span>.&rdquo;
        </p>
      </motion.blockquote>
    </SlideFrame>
  );
}

// ============ FEATURED IP · NEPHILIM ============
function SlideFeaturedIP() {
  const VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_cyber-forge-21/artifacts/x70kviq1_Anya_opening_eyes_with_sparks_202608110945.mp4";
  const tags = ["Fantasy", "Mythic", "Celestial", "Coming-of-Age"];
  const pipeline = [
    { stage: "Sleepy Owl", detail: "Manuscript · reader engagement", c: G },
    { stage: "Dream CapCha", detail: "Auto-generated cinematic scenes", c: "#7c3aed" },
    { stage: "Sunfield Studio", detail: "Green-light candidate · film / series", c: E },
  ];
  return (
    <section className="relative py-16 lg:py-24 border-b border-slate-800/80 overflow-hidden" data-testid="apex-featured-ip">
      <div className="absolute inset-0 te-grid-bg opacity-20" aria-hidden />
      <div className="absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full blur-[140px]" style={{ background: `${C}25` }} />
      <div className="absolute -bottom-24 -left-24 w-[500px] h-[500px] rounded-full blur-[140px]" style={{ background: `${G}20` }} />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-80px"}} transition={{duration:0.7}}>
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.3em] mb-4" style={{ color: G, background: `${G}0F`, border: `1px solid ${G}44` }}>
            <Sparkles size={11} /> // FEATURED IP · CROSS-SUBSIDIARY DEMO
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">
            One IP asset. <span style={{ color: C }}>Every subsidiary</span> pulls it forward.
          </h2>
          <p className="mt-4 text-slate-400 max-w-3xl leading-relaxed">
            Real example generated live on the platform. <span style={{ color: G }} className="font-semibold">Nephilim by Lao</span> — a fantasy manuscript that becomes an animated scene inside Dream CapCha, and enters Sunfield&apos;s green-light queue on the strength of its own viral traction.
          </p>
        </motion.div>

        <div className="mt-10 grid lg:grid-cols-[1.1fr_1fr] gap-6 items-start">
          {/* Video */}
          <motion.div initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-80px"}} transition={{duration:0.7}} className="relative" data-testid="apex-nephilim-wrap">
            <div className="absolute -inset-3 rounded-3xl blur-2xl opacity-70" style={{ background: `radial-gradient(circle at 30% 30%, ${C}44, transparent 60%), radial-gradient(circle at 70% 80%, ${G}33, transparent 60%)` }} aria-hidden />
            <div className="relative rounded-2xl overflow-hidden border-2 backdrop-blur-xl bg-slate-950 shadow-[0_20px_80px_-20px_rgba(0,0,0,0.7)]" style={{ borderColor: `${C}55` }}>
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-800/80 bg-slate-900/70">
                <div className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: C }}>
                  <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: C, boxShadow: `0 0 10px ${C}` }} />
                  LIVE SCENE · CH.01
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500">AI Video · Dream CapCha</div>
              </div>
              <video
                src={VIDEO}
                autoPlay
                muted
                loop
                playsInline
                controls
                preload="metadata"
                className="w-full aspect-video object-cover bg-black"
                data-testid="apex-nephilim-video"
              />
              <div className="px-4 py-3 border-t border-slate-800/80 bg-slate-900/40 flex items-center justify-between">
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.22em]" style={{ color: G }}>THE AWAKENING</div>
                  <div className="text-[12px] text-slate-300 mt-0.5">Anya opens her eyes with celestial sparks</div>
                </div>
                <Sparkles size={16} style={{ color: G }} />
              </div>
            </div>

            {/* Cross-subsidiary pipeline strip */}
            <div className="mt-4 rounded-2xl p-4 bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-3" style={{ color: C }}>// IP JOURNEY ACROSS SUBSIDIARIES</div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-2">
                {pipeline.map((p, i, arr) => (
                  <div key={i} className="contents">
                    <div className="rounded-lg p-2.5 border text-center" style={{ background: `${p.c}0F`, borderColor: `${p.c}55` }}>
                      <div className="font-mono text-[9px] uppercase tracking-[0.22em] font-bold" style={{ color: p.c }}>{p.stage}</div>
                      <div className="mt-0.5 text-[10px] text-slate-400">{p.detail}</div>
                    </div>
                    {i < arr.length - 1 && (
                      <div className="hidden sm:flex items-center justify-center">
                        <ArrowRight size={14} className="text-slate-600" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Book card */}
          <motion.div initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-80px"}} transition={{duration:0.7,delay:0.1}} className="rounded-2xl p-7 border-2 backdrop-blur-xl relative overflow-hidden" style={{ borderColor: `${C}55`, background: `linear-gradient(135deg, ${C}15, rgba(15,23,42,0.7))` }}>
            <div className="absolute -top-12 -right-12 h-48 w-48 rounded-full blur-3xl opacity-40" style={{ background: C }} aria-hidden />
            <div className="relative">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: G }}>
                <BookOpen size={11} /> ORIGINAL FICTION · SLEEPY OWL PUBLISHING
              </div>
              <h3 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.98]" style={{ color: C }}>
                Nephilim
              </h3>
              <div className="mt-3 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full flex items-center justify-center font-display text-sm font-bold text-slate-950" style={{ backgroundImage: `linear-gradient(135deg, ${C}, ${G})` }}>
                  L
                </div>
                <div className="text-[13px]">
                  <span className="font-mono uppercase tracking-[0.22em] text-slate-400 mr-1.5 text-[10px]">A story by</span>
                  <span className="font-display font-bold text-slate-100">Lao</span>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-1.5">
                {tags.map((t) => (
                  <span key={t} className="rounded-full px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em]" style={{ background: `${C}0F`, border: `1px solid ${C}55`, color: C }}>
                    {t}
                  </span>
                ))}
              </div>

              <div className="mt-6 pt-5 border-t border-slate-800/60">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-3" style={{ color: G }}>// SYNOPSIS</div>
                <p className="text-[13px] text-slate-300 leading-relaxed">
                  Born of an angel and a mortal, <span style={{ color: C }} className="font-semibold">Anya</span> grows up in the shadow of her father <span style={{ color: G }} className="font-semibold">Metatron&apos;s</span> grief over her late mother, Sarah. When Anya discovers Sarah was actually a descendant of an ancient bloodline of human <span className="font-semibold text-slate-100">Protectors</span>, she uncovers a legacy of intentional defiance against cosmic destiny. Blending raw celestial power with her mother&apos;s reality-bending protective abilities, Anya must embrace both sides of her heritage to stand against the celestial court and forge her own path.
                </p>
              </div>

              <div className="mt-6 pt-5 border-t border-slate-800/60 grid grid-cols-3 gap-2">
                <div className="rounded-xl p-3 border text-center" style={{ background: `${C}0F`, borderColor: `${C}55` }}>
                  <div className="font-display text-lg font-bold" style={{ color: C }}>Fiction</div>
                  <div className="mt-0.5 font-mono text-[8px] uppercase tracking-[0.22em] text-slate-500">Genre lane</div>
                </div>
                <div className="rounded-xl p-3 border text-center" style={{ background: `${G}0F`, borderColor: `${G}55` }}>
                  <div className="font-display text-lg font-bold" style={{ color: G }}>4</div>
                  <div className="mt-0.5 font-mono text-[8px] uppercase tracking-[0.22em] text-slate-500">Age bands live</div>
                </div>
                <div className="rounded-xl p-3 border text-center" style={{ background: `${E}0F`, borderColor: `${E}55` }}>
                  <div className="font-display text-lg font-bold" style={{ color: E }}>100%</div>
                  <div className="mt-0.5 font-mono text-[8px] uppercase tracking-[0.22em] text-slate-500">Rights retained</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ============ MAIN PAGE ============
export default function ApexPitch() {
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY) === "1") setUnlocked(true);
  }, []);
  useEffect(() => {
    document.title = "APEX · Holding Co. Vault";
    return () => { document.title = "TraceElements · We architect intelligent infrastructure."; };
  }, []);

  if (!unlocked) return <PinLock onUnlock={() => setUnlocked(true)} />;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="apex-pitch-page">
      <SlideCover />
      <SlideProblem />
      <SlideEcosystem />
      <SlideTech />
      <SlideFeaturedIP />
      <SlidePipeline />
      <SlideMVP />
      <SlideRevenue />
      <SlideAsk />

      <section className="py-8 bg-slate-950">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-4 pitch-no-print">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
            // CONFIDENTIAL · APEX Media Group Holding Co. · Prepared by TraceElements
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              data-testid="apex-download-pdf"
              className="inline-flex items-center gap-2 text-slate-950 hover:opacity-90 px-4 py-2 rounded-xl font-mono text-[10px] uppercase tracking-[0.22em] font-bold transition-all shadow-[0_0_20px_-6px_rgba(6,182,212,0.6)]"
              style={{ background: C }}
            >
              <Download size={12} /> Download PDF
            </button>
            <button
              onClick={() => exportPitchPptx({ vaultName: "APEX Holding Co", title: "APEX · Building the Disney of the digital-native era", subtitle: "Vertically integrated media holding company — investor deck.", brand: "06B6D4", accent: "F59E0B", slides: APEX_PPTX_SLIDES })}
              data-testid="apex-download-pptx"
              className="inline-flex items-center gap-2 border px-4 py-2 rounded-xl font-mono text-[10px] uppercase tracking-[0.22em] font-bold transition-all"
              style={{ borderColor: `${C}88`, background: `${C}1A`, color: C }}
            >
              <Download size={12} /> Download PPT
            </button>
            <button
              onClick={() => { sessionStorage.removeItem(STORAGE_KEY); window.location.reload(); }}
              data-testid="apex-relock"
              className="inline-flex items-center gap-2 border border-slate-800 bg-slate-900/60 text-slate-400 hover:text-slate-200 hover:border-slate-700 px-4 py-2 rounded-xl font-mono text-[10px] uppercase tracking-[0.22em] transition-all"
            >
              <Lock size={11} /> Relock Session
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
